/**
 * hr-cart-gifts.js — auto-adds free gifts to every cart over a threshold.
 *
 * Shopify's automatic "Buy X get Y" discount makes the gifts free but never
 * ADDS them to the cart. This script only adds/removes the gift lines; the
 * discount (Admin → Discounts) does all the pricing. If this script breaks,
 * the worst case is "no gift added" — it can never make anything free itself.
 *
 * On every page load + every cart change we sync:
 *   - non-gift total >= threshold → add each gift once (unless the customer
 *     removed it themselves)
 *   - below threshold / campaign off → remove gift lines
 *   - gift quantity > 1 → back to 1
 * Safety net: a gift line that isn't priced at $0 (discount off / not
 * applicable) is removed so nobody pays for a "free" item.
 *
 * Gift lines are identified by the private `_hr_gift` line property, so a
 * toothbrush the customer bought themselves is never touched.
 *
 * Config (window.HR_CART_GIFTS) is rendered by snippets/hr-cart-gifts.liquid.
 */
(function () {
  var config = window.HR_CART_GIFTS;
  if (!config) return;

  var GIFT_PROP = '_hr_gift';
  var DECLINED_KEY = 'hr-gifts-declined';      // gift ids the customer removed
  var BLOCKED_KEY = 'hr-gifts-blocked-total';  // total at which the discount refused
  var RELOAD_KEY = 'hr-gifts-reloaded-at';
  var RELOAD_COOLDOWN_MS = 5000;
  var CART_PATH = '/cart';

  var isSyncing = false;
  var hasPendingSync = false;

  /* ── storage helpers (sessionStorage can throw in private mode) ─────────── */

  function readStored(key, fallback) {
    try {
      var value = JSON.parse(sessionStorage.getItem(key));
      return value === null ? fallback : value;
    } catch (e) {
      return fallback;
    }
  }

  function writeStored(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable — worst case a removed gift comes back */
    }
  }

  /* ── cart API ───────────────────────────────────────────────────────────── */

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('/cart.js failed: ' + res.status);
      return res.json();
    });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error(url + ' failed: ' + res.status);
      return res.json();
    });
  }

  /* ── cart reading ───────────────────────────────────────────────────────── */

  function isGiftLine(line) {
    return Boolean(line.properties && line.properties[GIFT_PROP]);
  }

  /** Expedited-shipping fee line from the cart drawer toggle — not a product. */
  function isExpeditedLine(line) {
    return Boolean(line.properties && line.properties._hr_expedited);
  }

  /** What the customer actually pays for real products — gifts never count. */
  function qualifyingTotal(cart) {
    return cart.items
      .filter(function (line) { return !isGiftLine(line) && !isExpeditedLine(line); })
      .reduce(function (sum, line) { return sum + line.final_line_price; }, 0);
  }

  /* ── planning ───────────────────────────────────────────────────────────── */

  /**
   * Returns { updates: { lineKey: qty }, toAdd: [variantId] } — never mutates cart.
   */
  function planChanges(cart) {
    var giftLines = cart.items.filter(isGiftLine);
    var total = qualifyingTotal(cart);
    var qualifies = config.enabled && total >= config.threshold;
    var isBlocked = readStored(BLOCKED_KEY, null) === total;
    var declined = readStored(DECLINED_KEY, []);
    var updates = {};

    giftLines.forEach(function (line) {
      var isCurrentGift = config.variantIds.indexOf(line.variant_id) !== -1;
      if (!qualifies || !isCurrentGift) updates[line.key] = 0;
      else if (line.quantity > 1) updates[line.key] = 1;
    });

    var toAdd = qualifies && !isBlocked
      ? config.variantIds.filter(function (id) {
          var isInCart = giftLines.some(function (line) { return line.variant_id === id; });
          return !isInCart && declined.indexOf(id) === -1;
        })
      : [];

    return { updates: updates, toAdd: toAdd };
  }

  /** Gift lines the discount didn't make free. */
  function findUnpricedGifts(cart) {
    return cart.items.filter(function (line) {
      return isGiftLine(line) && line.final_line_price > 0;
    });
  }

  /* ── applying ───────────────────────────────────────────────────────────── */

  function applyPlan(plan) {
    var hasUpdates = Object.keys(plan.updates).length > 0;
    var hasAdds = plan.toAdd.length > 0;
    if (!hasUpdates && !hasAdds) return Promise.resolve(false);

    // Sequential, not parallel — concurrent cart writes can race each other.
    var step = hasUpdates
      ? postJSON('/cart/update.js', { updates: plan.updates })
      : Promise.resolve();

    return step
      .then(function () {
        if (!hasAdds) return null;
        return postJSON('/cart/add.js', {
          items: plan.toAdd.map(function (id) {
            var properties = {};
            properties[GIFT_PROP] = 'true';
            return { id: id, quantity: 1, properties: properties };
          }),
        });
      })
      .then(function () { return true; });
  }

  /** Safety net: remove gifts that aren't $0 and don't retry at this total. */
  function enforcePricing(cart) {
    var unpriced = findUnpricedGifts(cart);
    if (!unpriced.length) return Promise.resolve(false);

    console.warn('[hr-cart-gifts] discount not applied to gifts — removing them');
    writeStored(BLOCKED_KEY, qualifyingTotal(cart));

    var updates = {};
    unpriced.forEach(function (line) { updates[line.key] = 0; });
    return postJSON('/cart/update.js', { updates: updates }).then(function () { return true; });
  }

  /* ── UI refresh ─────────────────────────────────────────────────────────── */

  function refreshUI() {
    var isCartPage = window.location.pathname === CART_PATH;
    if (isCartPage) {
      // hr-cart is server-rendered; reload so gift lines + totals show.
      // Cooldown guards against a reload loop if the cart never settles.
      var lastReload = readStored(RELOAD_KEY, 0);
      if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) return Promise.resolve();
      writeStored(RELOAD_KEY, Date.now());
      window.location.reload();
      return Promise.resolve();
    }
    // hr-cart-drawer.js re-renders itself (and the header count) on this.
    document.dispatchEvent(new CustomEvent('hr:cart-refresh'));
    if (typeof window.hrUpdateCartCount !== 'function') return Promise.resolve();
    // Both helpers come from hr-cart-add.js; the badge excludes the expedited fee.
    return getCart().then(function (cart) { window.hrUpdateCartCount(window.hrCartProductCount(cart)); });
  }

  /* ── sync loop ──────────────────────────────────────────────────────────── */

  function sync() {
    if (isSyncing) {
      hasPendingSync = true; // a cart change landed mid-sync — run again after
      return;
    }
    isSyncing = true;

    var hasChanged = false;
    getCart()
      .then(function (cart) { return applyPlan(planChanges(cart)); })
      .then(function (changed) {
        hasChanged = changed;
        return changed ? getCart().then(enforcePricing) : false;
      })
      .then(function (removedUnpriced) {
        if (hasChanged || removedUnpriced) return refreshUI();
        return null;
      })
      .catch(function (err) {
        // Never block the customer's own cart over a gift.
        console.error('[hr-cart-gifts]', err);
      })
      .then(function () {
        isSyncing = false;
        if (hasPendingSync) {
          hasPendingSync = false;
          sync();
        }
      });
  }

  /* ── listeners ──────────────────────────────────────────────────────────── */

  // Remember gifts the customer removes on the cart page (capture phase so
  // it runs before Horizon's handler re-renders the row).
  document.addEventListener(
    'click',
    function (event) {
      var removeButton = event.target.closest('[data-hr-gift-remove]');
      if (!removeButton) return;
      var variantId = Number(removeButton.getAttribute('data-hr-gift-remove'));
      var declined = readStored(DECLINED_KEY, []);
      if (declined.indexOf(variantId) === -1) writeStored(DECLINED_KEY, declined.concat(variantId));
    },
    true
  );

  document.addEventListener('cart:update', sync);   // Horizon cart page (qty / remove)
  document.addEventListener('hr:cart-added', sync); // hr-cart-add.js product cards
  document.addEventListener('hr:cart-changed', sync); // hr-cart-drawer.js

  sync();
})();
